const API = (import.meta.env.VITE_API_URL as string | undefined) ?? "http://localhost:8787";
export const mediaUrl = (key: string) => `${(import.meta.env.VITE_MEDIA_URL as string | undefined) ?? `${API}/media`}/${key}`;

// ---- sign-in: a bearer token kept on the device (cookies cannot reach the API from the iPhone/Android shells) ----
const TOKEN_KEY = "sekani.session";
export const getToken = (): string | null => { try { return localStorage.getItem(TOKEN_KEY); } catch { return null; } };
export const setToken = (t: string | null) => { try { t ? localStorage.setItem(TOKEN_KEY, t) : localStorage.removeItem(TOKEN_KEY); } catch { /* storage unavailable: signed in for this page only */ } };
const bearer = (): Record<string, string> => { const t = getToken(); return t ? { authorization: `Bearer ${t}` } : {}; };

export class ApiError extends Error {
  constructor(public code: string, message: string, public status: number) { super(message); }
}

export async function api<T>(path: string, init: { method?: string; body?: unknown; token?: string } = {}): Promise<T> {
  const res = await fetch(`${API}/v1${path}`, {
    method: init.method ?? (init.body ? "POST" : "GET"),
    headers: { ...(init.body ? { "content-type": "application/json" } : {}), ...(init.token ? { "x-booking-token": init.token } : {}), ...bearer() },
    body: init.body ? JSON.stringify(init.body) : undefined,
  });
  if (!res.ok) {
    const j = (await res.json().catch(() => null)) as { error?: { code?: string; message?: string } } | null;
    throw new ApiError(j?.error?.code ?? "http_error", j?.error?.message ?? res.statusText, res.status);
  }
  return (await res.json()) as T;
}

/** Raw-image upload (a payment screenshot), authorised by the booking link. Returns the private storage key. */
export async function uploadProof(bookingId: string, token: string, image: Blob): Promise<string> {
  const res = await fetch(`${API}/v1/guest/bookings/${bookingId}/proof`, { method: "POST", headers: { "content-type": image.type, "x-booking-token": token }, body: image });
  if (!res.ok) {
    const j = (await res.json().catch(() => null)) as { error?: { code?: string; message?: string } } | null;
    throw new ApiError(j?.error?.code ?? "http_error", j?.error?.message ?? res.statusText, res.status);
  }
  return ((await res.json()) as { key: string }).key;
}

/**
 * Phone screenshots are often 3–8 MB; on a weak connection that is the difference between sending and not.
 * Re-encode to a 1600px-wide JPEG. If anything fails the original is sent untouched.
 */
export async function shrinkImage(file: File): Promise<Blob> {
  try {
    const bmp = await createImageBitmap(file);
    const scale = Math.min(1, 1600 / bmp.width);
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(bmp.width * scale);
    canvas.height = Math.round(bmp.height * scale);
    canvas.getContext("2d")!.drawImage(bmp, 0, 0, canvas.width, canvas.height);
    const out = await new Promise<Blob | null>((r) => canvas.toBlob(r, "image/jpeg", 0.85));
    return out && out.size < file.size ? out : file;
  } catch { return file; }
}

// Shapes the screens rely on (the API returns more; this is the subset we read).
export interface Listing {
  id: string; title: { fr: string; ar?: string; en?: string }; description?: { fr: string; ar?: string; en?: string };
  cityKey: string; maxGuests: number; bedrooms: number; beds: number; basePriceMinor: number; currency: string;
  ratingAvg: number | null; reviewCount: number; photos: string[];
}
export interface ListingPage extends Omit<Listing, "photos"> {
  photos: { key: string }[]; videos?: { key: string }[]; amenities: { id: number; key: string }[];
  minNights: number; maxNights: number; cleaningFeeMinor: number;
}
export interface Quote {
  bookable: boolean; problems: string[]; currency: string;
  breakdown: { nights: number; totalMinor: number; depositMinor: number } | null;
}
export interface BookingDetail {
  support: { whatsapp: string | null };
  booking: {
    id: string; code: string; status: string; checkIn: string; checkOut: string; nights: number; totalMinor: number; depositMinor: number; currency: string; payBy: string | null;
    createdAt: string; acceptedAt: string | null; confirmedAt: string | null; checkedInAt: string | null; completedAt: string | null;
  };
  payments: { id: string; kind: string; status: string; amountMinor: number }[];
  listing: { id: string; title: { fr: string; ar?: string; en?: string }; address?: string; checkInTime: string; checkOutTime: string };
}
export interface Stay {
  id: string; code: string; status: string; listingTitle: { fr: string; ar?: string; en?: string } | null; coverKey: string | null;
  checkIn: string; checkOut: string; nights: number; totalMinor: number; currency: string; createdAt: string;
}

// ---- remembered bookings (guest checkout has no login: the link token is the key) ----
const KEY = "sekani.bookings";
export type Saved = { id: string; token: string; code: string };
export function savedBookings(): Saved[] {
  try { return JSON.parse(localStorage.getItem(KEY) ?? "[]") as Saved[]; } catch { return []; }
}
export function saveBooking(s: Saved) {
  try { localStorage.setItem(KEY, JSON.stringify([s, ...savedBookings().filter((x) => x.id !== s.id)].slice(0, 20))); } catch { /* ignore */ }
}

/** Better Auth lives beside /v1, at /auth. A successful sign-in/up returns the session token in `set-auth-token`. */
export async function auth<T = unknown>(path: string, body?: unknown): Promise<T> {
  const res = await fetch(`${API}/auth${path}`, { method: body === undefined ? "GET" : "POST", headers: { ...(body === undefined ? {} : { "content-type": "application/json" }), ...bearer() }, body: body === undefined ? undefined : JSON.stringify(body) });
  if (!res.ok) {
    const j = (await res.json().catch(() => null)) as { code?: string; message?: string } | null;
    throw new ApiError(j?.code ?? "auth_failed", j?.message ?? res.statusText, res.status);
  }
  const t = res.headers.get("set-auth-token");
  if (t) setToken(t);
  return (await res.json().catch(() => null)) as T;
}

/** Signed-in image upload (listing photos, ID documents, payment screenshots). Returns the storage key. */
export async function uploadImage(area: "listing" | "id" | "proof" | "video", image: Blob): Promise<string> {
  const res = await fetch(`${API}/v1/media/${area}`, { method: "POST", headers: { "content-type": image.type || "image/jpeg", ...bearer() }, body: image });
  if (!res.ok) {
    const j = (await res.json().catch(() => null)) as { error?: { code?: string; message?: string } } | null;
    throw new ApiError(j?.error?.code ?? "http_error", j?.error?.message ?? res.statusText, res.status);
  }
  return ((await res.json()) as { key: string }).key;
}
